import { DIFFICULTIES, DIFFICULTY_NAMES, MODE_NAMES, MODES, type Difficulty, type Mode } from "@eldrun/content";
import type { MatchSession } from "../../net/match-client.ts";
import { h, mount } from "../kit/dom.ts";
import type { ClassSelect } from "./class-select.ts";
import { lobbyView } from "./lobby-model.ts";

/** Última projeção desenhada em cada painel (para não redesenhar sem mudança visível). */
const drawn = new WeakMap<HTMLElement, string>();

/** Bolinha da cor do jogador (a da classe, F85). A cor vai pelo CSSOM: a CSP do build não aceita o atributo `style`. */
function dot(color: string): HTMLElement {
  const el = h("span", { class: "dot" });
  el.style.background = color;
  return el;
}

/**
 * Tela de lobby (F85): a escolha 3D da classe (`ClassSelect`, a mesma do "Jogar sozinho"), com a escolha e
 * o pronto de cada um, e o painel da sala ao lado. As colunas da escolha só se atualizam no lugar; o
 * painel é redesenhado só quando a projeção muda (redesenhar a cada patch trocava os botões sob o
 * ponteiro e apagava a seed que o dono digitava), e o foco nele volta ao mesmo controle.
 */
export function renderLobby(container: HTMLElement, session: MatchSession, onLeave: () => void, select: ClassSelect): void {
  const view = lobbyView(session.state, session.sessionId);
  select.update(view.myClass, view.players);
  let host = container.querySelector<HTMLElement>(":scope > .escolha-sala");
  if (!host || select.root.parentElement !== container) {
    host = h("div", { class: "escolha-sala" });
    mount(container, select.root, host);
  }
  const key = JSON.stringify(view);
  if (drawn.get(host) === key && host.querySelector("#jogadores")) return;
  drawn.set(host, key);
  // O texto digitado na seed e o foco (pelo id do controle, ou o "Remover" de quem) sobrevivem ao redesenho.
  const previous = host.querySelector<HTMLInputElement>("#seed");
  const typed = previous?.value ?? "";
  const active = document.activeElement as HTMLElement | null;
  const hadFocus = !!active && host.contains(active);
  const focusKey = !hadFocus ? "" : active.id ? `#${CSS.escape(active.id)}` : active.dataset["remover"] ? `[data-remover="${CSS.escape(active.dataset["remover"])}"]` : "";
  const seedInput = h("input", { id: "seed", maxlength: 32, placeholder: "seed (vazio = sorteada)", class: "grow" });
  seedInput.value = typed;

  const copyCode = () => {
    void navigator.clipboard?.writeText(view.code).catch(() => {});
  };

  const configRow =
    view.amOwner && view.isPrivate
      ? h(
          "div",
          { class: "row" },
          h(
            "select",
            { id: "cfg-modo", onchange: (e: Event) => session.configure({ mode: (e.target as HTMLSelectElement).value as Mode }) },
            ...MODES.map((m) => h("option", { value: m, selected: m === view.mode }, MODE_NAMES[m])),
          ),
          h(
            "select",
            { id: "cfg-dificuldade", onchange: (e: Event) => session.configure({ difficulty: (e.target as HTMLSelectElement).value as Difficulty }) },
            ...DIFFICULTIES.map((d) => h("option", { value: d, selected: d === view.difficulty }, DIFFICULTY_NAMES[d])),
          ),
          seedInput,
          h("button", { id: "btn-usar-seed", class: "small", onclick: () => session.configure({ seed: seedInput.value }) }, "Usar seed"),
          view.mode === "mutirao"
            ? h(
                "label",
                { class: "check" },
                h("input", {
                  type: "checkbox",
                  id: "cfg-fogo-amigo",
                  checked: view.friendlyFire,
                  onchange: (e: Event) => session.configure({ friendlyFire: (e.target as HTMLInputElement).checked }),
                }),
                "Fogo amigo",
              )
            : null,
        )
      : null;

  mount(
    host,
    h(
      "section",
      { class: "panel", "aria-label": "Lobby" },
      h("h2", {}, view.isPrivate ? "Sala privada" : "Partida pública"),
      h(
        "div",
        { class: "row" },
        h("span", { class: "hint" }, "Código"),
        h("span", { class: "code", id: "codigo-sala" }, view.code),
        h("button", { id: "btn-copiar", class: "small", onclick: copyCode, title: "Copiar código" }, "Copiar"),
      ),
      h(
        "p",
        { class: "hint" },
        `${MODE_NAMES[view.mode as Mode] ?? view.mode} · ${DIFFICULTY_NAMES[view.difficulty as Difficulty] ?? view.difficulty} · ${view.pvp} · seed ${view.seedCode}`,
      ),
      configRow,
      h(
        "ul",
        { class: "players", id: "jogadores" },
        ...view.players.map((p) =>
          h(
            "li",
            { "data-session": p.sessionId },
            dot(p.color),
            h("span", { class: "slot-n" }, String(p.n)),
            h("span", { class: "grow" }, p.name, p.isMe ? " (você)" : "", h("span", { class: "classe-nome" }, ` · ${p.className}`)),
            p.isOwner ? h("span", { class: "tag" }, "dono") : null,
            h("span", { class: p.ready ? "ready" : "waiting" }, p.ready ? "pronto" : "esperando"),
            view.amOwner && !p.isMe ? h("button", { class: "small", "data-remover": p.sessionId, onclick: () => session.kick(p.sessionId) }, "Remover") : null,
          ),
        ),
      ),
      view.countdown > 0 ? h("p", { class: "countdown", id: "contagem" }, `Começando em ${view.countdown}…`) : null,
      h(
        "div",
        { class: "row" },
        view.amOwner
          ? h("button", { id: "btn-iniciar", class: "primary grow", disabled: !view.canStart, onclick: () => session.start() }, "Iniciar partida")
          : h(
              "button",
              { id: "btn-pronto", class: `grow ${view.meReady ? "" : "primary"}`, onclick: () => session.setReady(!view.meReady) },
              view.meReady ? "Cancelar pronto" : "Estou pronto",
            ),
        h("button", { id: "btn-sair-sala", onclick: onLeave }, "Sair da sala"),
      ),
      view.amOwner && view.blocker ? h("p", { class: "hint", id: "bloqueio" }, view.blocker) : null,
    ),
  );
  if (!hadFocus) return;
  const target = focusKey ? host.querySelector<HTMLElement>(focusKey) : null;
  // O controle que tinha o foco sumiu ou travou (o "Remover" de quem saiu, o "Iniciar" desligado): volta à escolha.
  if (target && !(target as HTMLButtonElement).disabled) target.focus();
  else select.focusChosen();
}
